import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import styles from "./Modal.module.css";

/** Panel widths. `sm` is a single-column form, `lg` a list plus a form. */
export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  /** The dialog's accessible name. Shown as the header title unless `headerSlot` replaces it. */
  title: string;
  /** Optional second line under the title — the record the dialog acts on. */
  subtitle?: string;
  /**
   * Replaces the visible title/subtitle block with custom header content (e.g. a
   * tab strip), keeping the close button. `title` is still used as the dialog's
   * accessible name.
   */
  headerSlot?: ReactNode;
  /** Panel width. Defaults to `md`. */
  size?: ModalSize;
  /** Called when the overlay is clicked, Escape is pressed, or close is invoked. */
  onClose: () => void;
  children: ReactNode;
  /** Optional pinned action row rendered below the scrolling body. */
  footer?: ReactNode;
}

/** Everything that can hold focus inside the panel, in document order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal shell: dimmed overlay, centred panel, Escape-to-close,
 * overlay-click-to-close, a Tab focus trap, body scroll lock, and focus returned
 * to the trigger on close. Presentation only — the caller supplies the body.
 *
 * Every dialog in the app renders through this, so the overlay scrim, panel
 * radius, header rule and close affordance are defined once. Screens that used
 * to hand-roll their own overlay drifted on all four and shipped no Escape key,
 * no scroll lock and no focus management.
 */
export function Modal({ title, subtitle, headerSlot, size = "lg", onClose, children, footer }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Overlay clicks must only close when the gesture *started* on the overlay;
  // otherwise selecting text in the panel and releasing outside it closes the
  // dialog and discards the form.
  const overlayMouseDown = useRef(false);
  const titleId = useId();
  const subtitleId = useId();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const targets = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (node) => node.offsetParent !== null || node === document.activeElement,
      );
      if (targets.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = targets[0];
      const last = targets[targets.length - 1];
      const active = document.activeElement;
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      }
    },
    [onClose],
  );

  // Focus, scroll lock and focus-restore run once per open. Keying this off
  // `handleKeyDown` (which changes identity whenever the caller's `onClose`
  // prop does — e.g. on every controlled-input keystroke) re-focused the
  // panel's first control on each render, yanking focus out of form inputs.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Land on the first control rather than the panel, so a form dialog is
    // typeable immediately; fall back to the panel when there is nothing.
    const panel = panelRef.current;
    const firstControl = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (firstControl ?? panel)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleOverlayMouseDown = (event: MouseEvent): void => {
    overlayMouseDown.current = event.target === event.currentTarget;
  };

  const handleOverlayClick = (event: MouseEvent): void => {
    if (event.target !== event.currentTarget) return;
    if (!overlayMouseDown.current) return;
    overlayMouseDown.current = false;
    onClose();
  };

  // Rendered at <body> so the fixed overlay is positioned against the viewport,
  // never a transformed or scroll-clipped ancestor (the board's card layer is
  // both). The focus trap and Escape handling above are unaffected by the move.
  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={panelRef}
        className={cn(styles.panel, styles[size])}
        role="dialog"
        aria-modal="true"
        aria-label={headerSlot ? title : undefined}
        aria-labelledby={headerSlot ? undefined : titleId}
        aria-describedby={!headerSlot && subtitle ? subtitleId : undefined}
        tabIndex={-1}
      >
        <header className={styles.header}>
          {headerSlot ?? (
            <div className={styles.heading}>
              <h2 className={styles.title} id={titleId}>
                {title}
              </h2>
              {subtitle && (
                <p className={styles.subtitle} id={subtitleId}>
                  {subtitle}
                </p>
              )}
            </div>
          )}
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                d="M4 4l8 8M12 4l-8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
